/**
 * Who may create a project in a workspace (001 § Permissions): a workspace owner or admin
 * always, a member when the workspace's `members_can_create_projects` is on. Read by the
 * sidebar's New project row, the palette's Create project action and the projects index.
 */
export function canCreateProject(me: { readonly memberships: readonly { readonly slug: string; readonly role: string }[] } | undefined, ws: string, membersCanCreate: boolean | undefined): boolean {
  if (me === undefined) return false;
  const membership = me.memberships.find((m) => m.slug === ws);
  if (membership === undefined) return false;
  return membership.role === "owner" || membership.role === "admin" || membersCanCreate === true;
}

/**
 * Whether the caller holds a workspace role other than guest (020 `contracts/http-api.md`
 * § Permissions: "any W role except guest" reads accounts and account groups). It shows the Clients
 * row and the palette's two client entries, absent rather than disabled for a guest.
 */
export function isWorkspaceMember(me: { readonly memberships: readonly { readonly slug: string; readonly role: string }[] } | undefined, ws: string): boolean {
  const role = me?.memberships.find((m) => m.slug === ws)?.role;
  return role === "owner" || role === "admin" || role === "member";
}

/**
 * Whether the caller is an owner or admin of the workspace (001 § Permissions: the row "edit workspace
 * settings, invite, change roles, agents, webhooks, export"). It shows the write controls and the
 * pages only those roles can read (invites, webhooks, agent tokens), hidden rather than disabled for
 * everyone else (`contracts/screens.md` § Workspace settings); the server still decides every write.
 */
export function isWorkspaceAdmin(me: { readonly memberships: readonly { readonly slug: string; readonly role: string }[] } | undefined, ws: string): boolean {
  const role = me?.memberships.find((m) => m.slug === ws)?.role;
  return role === "owner" || role === "admin";
}
